/*
  The game page's A-Z index over a game's sets.

  Two pieces, both pure, because the component around them cannot be tested (RTL 13
  against React 17, spec §2):

    - `groupSetsAlphabetically` adapts a Set to the shared grouping helper. Sets carry
      `setName` where games carry `name`, and that is the whole difference — the folding,
      the "#" bucket and its sort-last rule are HomeView's, unchanged, so the two indexes
      cannot drift apart. The name grouped on is the DISPLAY name where there is one
      (pop-language-split D10): a set filed under the letter it is not shown starting with
      would look misfiled.
    - `buildLetterBar` turns those groups into the fixed A-Z strip. It is fixed on
      purpose: a bar that only showed the letters a game happens to have would move its
      buttons under the reader's cursor as the filter narrows the list.

  Feed it `combinedSets`, never `allSets` — D30 merges same-named language editions and
  sums their counts, and an index built off the raw rows would list a set once per
  language.
*/

import {
  AlphabeticalGroup,
  NamedGame,
  OTHER_GROUP_KEY,
  groupGamesAlphabetically,
} from "./grouping";

import { Set } from "types/gameInfo";

/* A Set carrying the `name` the shared grouping helper sorts and buckets on. */
export interface IndexedSet extends Set {
  name: string;
}

export const groupSetsAlphabetically = (
  sets: readonly Set[]
): AlphabeticalGroup<IndexedSet>[] =>
  groupGamesAlphabetically(sets.map((set) => ({ ...set, name: set.displayName ?? set.setName })));

export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export interface LetterBarEntry {
  key: string;
  count: number;
}

/*
  Always the 26 letters; "#" only when something is in it.

  "#" is the one key that is not always meaningful — most games have no set whose name
  starts outside A-Z — and a permanently dead button at the end of the strip reads as a
  bug. A letter with no sets is different: its emptiness is information ("this game has
  nothing under Q"), and its position is what keeps the strip stable.
*/
export const buildLetterBar = <T extends NamedGame>(
  groups: readonly AlphabeticalGroup<T>[]
): LetterBarEntry[] => {
  const counts = new Map<string, number>(
    groups.map((group) => [group.key, group.games.length])
  );

  const entries: LetterBarEntry[] = ALPHABET.map((key) => ({
    key,
    count: counts.get(key) ?? 0,
  }));

  const other = counts.get(OTHER_GROUP_KEY) ?? 0;
  if (other > 0) {
    entries.push({ key: OTHER_GROUP_KEY, count: other });
  }

  return entries;
};

/*
  The DOM id for a letter's section. "#" is a legal id character but a hostile one to
  select for, so the bucket gets a word instead of its symbol.
*/
export const letterSectionId = (key: string): string =>
  `sets-letter-${key === OTHER_GROUP_KEY ? "other" : key.toLowerCase()}`;
